
import ServiceNavbar from '../components/ServiceNavbar';
import Footer from '../../home/components/Footer';
import { Link } from 'react-router-dom';

export default function InfluencerMarketing() {
  const features = [
    {
      icon: 'ri-search-eye-line',
      title: 'Influencer Discovery',
      description: 'We identify and vet influencers who align with your brand values and reach your target audience.'
    },
    {
      icon: 'ri-handshake-line',
      title: 'Partnership Management',
      description: 'End-to-end management of influencer relationships, contracts, and deliverables.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Campaign Strategy',
      description: 'Custom campaign concepts designed to maximize reach, engagement, and conversions.'
    },
    {
      icon: 'ri-creative-commons-line',
      title: 'Content Collaboration',
      description: 'Creative briefs and guidance to ensure authentic content that resonates.'
    },
    {
      icon: 'ri-pie-chart-line',
      title: 'Performance Tracking',
      description: 'Real-time monitoring and detailed reporting on campaign performance metrics.'
    },
    {
      icon: 'ri-scales-3-line',
      title: 'ROI Optimization',
      description: 'Continuous optimization to maximize return on your influencer investment.'
    }
  ];

  const influencerTiers = [
    { tier: 'Nano', followers: '1K-10K', benefit: 'High engagement, niche audiences', icon: 'ri-user-star-line' },
    { tier: 'Micro', followers: '10K-100K', benefit: 'Authentic connections, cost-effective', icon: 'ri-group-line' },
    { tier: 'Macro', followers: '100K-1M', benefit: 'Wide reach, professional content', icon: 'ri-team-line' },
    { tier: 'Mega', followers: '1M+', benefit: 'Massive exposure, brand prestige', icon: 'ri-vip-crown-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <ServiceNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center pt-28">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/84779892d0fc76e8eef790365b199eb5.jpg"
            alt="Influencer Marketing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00989e]/20 rounded-full mb-6">
              <i className="ri-user-heart-line text-[#00989e]"></i>
              <span className="text-[#00b4a6] text-sm font-medium">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Influencer Marketing
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Strategic influencer partnerships that connect your brand with authentic voices and engaged audiences who trust their recommendations.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="px-8 py-4 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap">
                Find Influencers
              </button>
              <Link to="/case-studies" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap">
                View Campaigns
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Influencer Tiers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Influencer Tiers We Work With
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From nano to mega influencers, we match you with the right partners for your goals
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {influencerTiers.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#00989e]/10 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-[#00989e]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{item.tier}</h3>
                <div className="text-[#00989e] font-semibold mb-3">{item.followers} followers</div>
                <p className="text-gray-600 text-sm">{item.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Approach
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A comprehensive influencer marketing service from discovery to results
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#00989e]/10 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-[#00989e]`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Results That Speak
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Our influencer campaigns consistently outperform industry benchmarks, delivering authentic engagement and measurable business results.
              </p>
              <div className="grid grid-cols-2 gap-8">
                {[
                  { value: '8.5%', label: 'Avg. Engagement Rate' },
                  { value: '3.2x', label: 'ROI on Campaigns' },
                  { value: '500+', label: 'Influencer Network' },
                  { value: '50M+', label: 'Total Reach' }
                ].map((stat, index) => (
                  <div key={index}>
                    <div className="text-4xl font-bold text-[#00b4a6] mb-2">{stat.value}</div>
                    <div className="text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Influencer%20creating%20content%20with%20smartphone%20and%20ring%20light%2C%20modern%20home%20studio%20setup%2C%20lifestyle%20photography%20aesthetic%2C%20teal%20and%20mint%20accent%20colors%20in%20background%2C%20professional%20content%20creation%20environment&width=600&height=500&seq=influencer-results-1&orientation=landscape"
                alt="Influencer Content Creation"
                className="w-full h-[500px] object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#00989e] to-[#007a7f]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Partner with Influencers?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's find the perfect voices to amplify your brand message.
          </p>
          <button className="px-10 py-4 bg-white text-[#00989e] font-bold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
            Start Your Campaign
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
